import React, { useEffect } from "react";
import { Flame, X, Sparkles } from "lucide-react";

/**
 * Streak reward modal — Warren shout-out that fires the first time a founder
 * crosses a milestone streak (7 days, 30 days). Kept intentionally warm and
 * short: badge + a couple of lines from Warren + a single "Keep the flame"
 * close button.
 */

const REWARDS = {
  7: {
    badge: "Weeklong Flame",
    tier: "bronze",
    gradient: "linear-gradient(135deg, #FDBA74 0%, #F97316 55%, #EA580C 100%)",
    heading: "Seven days in a row, founder.",
    quote:
      "Real brands aren't built in bursts — they're built in reps. You just proved you can string a full week together. That's the exact muscle that separates the founders who ship from the ones who plan.",
    cta: "Keep the flame",
  },
  30: {
    badge: "Thirty-Day Founder",
    tier: "gold",
    gradient: "linear-gradient(135deg, #FDE68A 0%, #E91E63 45%, #B45309 100%)",
    heading: "Thirty days. That's a habit.",
    quote:
      "This is the number where most founders quit and most brands die. You didn't. Show up like this for another 60 days and you'll be shocked at what your business looks like. I mean that.",
    cta: "Keep going",
  },
};

export default function StreakRewardModal({ milestone, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const reward = REWARDS[milestone];
  if (!reward) return null;

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="streak-reward-title"
      data-testid="streak-reward-modal"
      data-milestone={String(milestone)}
    >
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full text-white/80 hover:text-white hover:bg-black/20 transition"
          data-testid="streak-reward-close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Badge header */}
        <div
          className="px-7 pt-8 pb-7 text-center text-white relative"
          style={{ background: reward.gradient }}
        >
          <div className="absolute inset-0 opacity-20" style={{
            background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.6) 0%, transparent 40%)"
          }} />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 shadow-inner">
              <Flame className="w-10 h-10 text-white drop-shadow" />
            </div>
            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-black/20 backdrop-blur-sm px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em]">
              <Sparkles className="w-3 h-3" /> Badge Unlocked
            </div>
            <p
              className="mt-3 font-display text-2xl leading-tight"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              {reward.badge}
            </p>
            <p className="mt-1 text-xs uppercase tracking-[0.22em] text-white/80">
              {milestone}-day streak
            </p>
          </div>
        </div>

        {/* Warren shout-out */}
        <div className="p-7">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-fuchsia-700">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-fuchsia-600" />
            Morgan says
          </div>
          <h3
            id="streak-reward-title"
            className="mt-3 font-display text-xl leading-tight text-slate-900"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            {reward.heading}
          </h3>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            {reward.quote}
          </p>

          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full inline-flex items-center justify-center rounded-full px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-md hover:shadow-lg transition"
            style={{
              background: "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)",
            }}
            data-testid="streak-reward-cta"
          >
            {reward.cta}
          </button>
        </div>
      </div>
    </div>
  );
}
